import { useState } from "react";
import { useNavigate, Link, useLocation, Navigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Mail, Lock, ShieldCheck, Sparkles, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import tr from "@/i18n/tr";
import Logo from "@/components/common/Logo";

export default function LoginPage() {
    const { login, isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const fromPath = location.state?.from?.pathname;
    const defaultDest = (u) => (u?.role === "client" ? "/portal" : "/dashboard");
    const from = fromPath && fromPath !== "/login" ? fromPath : defaultDest(user);

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    if (isAuthenticated) return <Navigate to={from} replace />;

    const validate = () => {
        const e = {};
        if (!email) e.email = tr.validation.required;
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = tr.validation.emailInvalid;
        if (!password) e.password = tr.validation.required;
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setLoading(true);
        try {
            const loggedInUser = await login({ email: email.trim().toLowerCase(), password, rememberMe });
            toast.success(`${tr.login.welcomeBack}!`);
            const dest = fromPath && fromPath !== "/login" ? fromPath : defaultDest(loggedInUser);
            navigate(dest, { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || tr.login.errorInvalid;
            toast.error(msg);
            setErrors({ form: msg });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full grid lg:grid-cols-[1.05fr_1fr] bg-background">
            {/* Left hero */}
            <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-sidebar text-sidebar-foreground p-12 xl:p-16">
                {/* Decorative */}
                <div aria-hidden className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-[hsl(var(--brand))]/25 blur-3xl" />
                <div aria-hidden className="pointer-events-none absolute bottom-0 right-0 h-[520px] w-[520px] rounded-full bg-[hsl(var(--brand))]/10 blur-3xl" />
                <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '28px 28px' }} />

                <div className="relative flex items-center justify-between">
                    <Logo variant="light" />
                    <span className="text-[11px] tracking-[0.2em] uppercase text-sidebar-muted">v1.0 • Phase 01</span>
                </div>

                <div className="relative max-w-lg">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 backdrop-blur px-3 py-1.5 text-[11px] font-medium text-sidebar-foreground/90 mb-6">
                        <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--brand))]" />
                        Premium Agency ERP
                    </div>
                    <h1 className="text-4xl xl:text-5xl font-bold leading-[1.1] tracking-tight text-white">
                        {tr.login.heroTitle}
                    </h1>
                    <p className="mt-5 text-[15px] leading-relaxed text-sidebar-muted">
                        {tr.login.heroSubtitle}
                    </p>

                    <div className="mt-10 grid grid-cols-3 gap-3">
                        {[
                            { Icon: TrendingUp, label: "Finansal Görünürlük" },
                            { Icon: ShieldCheck, label: "Kurumsal Güvenlik" },
                            { Icon: Sparkles,    label: "AI Destekli Akışlar" },
                        ].map(({ Icon, label }) => (
                            <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                                <Icon className="h-4 w-4 text-[hsl(var(--brand))] mb-2" />
                                <div className="text-[12px] font-medium leading-snug text-sidebar-foreground/90">{label}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="relative flex items-center justify-between text-[11px] text-sidebar-muted">
                    <span>© {new Date().getFullYear()} OMB Agency</span>
                    <span>{tr.login.footer}</span>
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center p-6 sm:p-10">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-8 flex justify-center">
                        <Logo variant="dark" />
                    </div>

                    <div className="mb-8">
                        <h2 className="text-[28px] font-bold tracking-tight text-foreground">{tr.login.title}</h2>
                        <p className="mt-2 text-[14.5px] text-muted-foreground">{tr.login.subtitle}</p>
                    </div>

                    <form onSubmit={onSubmit} className="space-y-4" noValidate data-testid="login-form">
                        {/* Email */}
                        <div className="space-y-1.5">
                            <label htmlFor="email" className="block text-[13px] font-semibold text-foreground">
                                {tr.common.email}
                            </label>
                            <div className="relative">
                                <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <input
                                    id="email"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={tr.login.emailPlaceholder}
                                    data-testid="login-email"
                                    className="h-11 w-full rounded-xl border border-border bg-background pl-10 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40 transition"
                                />
                            </div>
                            {errors.email && <p className="text-xs text-[hsl(var(--destructive))]">{errors.email}</p>}
                        </div>

                        {/* Password */}
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <label htmlFor="password" className="block text-[13px] font-semibold text-foreground">
                                    {tr.common.password}
                                </label>
                                <Link
                                    to="/forgot-password"
                                    data-testid="login-forgot-link"
                                    className="text-[12.5px] font-semibold text-[hsl(var(--brand))] hover:underline"
                                >
                                    {tr.common.forgotPassword}
                                </Link>
                            </div>
                            <div className="relative">
                                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder={tr.login.passwordPlaceholder}
                                    data-testid="login-password"
                                    className="h-11 w-full rounded-xl border border-border bg-background pl-10 pr-11 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40 transition"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((s) => !s)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
                                    aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                                    data-testid="login-password-toggle"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.password && <p className="text-xs text-[hsl(var(--destructive))]">{errors.password}</p>}
                        </div>

                        {/* Remember me */}
                        <div className="flex items-center justify-between pt-1">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    data-testid="login-remember"
                                    className="h-4 w-4 rounded border-border text-[hsl(var(--brand))] focus:ring-brand/40 accent-[hsl(var(--brand))]"
                                />
                                <span className="text-[13px] text-foreground">{tr.common.rememberMe}</span>
                            </label>
                        </div>

                        {errors.form && (
                            <div className="rounded-xl border border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5 px-3 py-2 text-[12.5px] text-[hsl(var(--destructive))]">
                                {errors.form}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit"
                            className="btn-brand w-full h-11 text-[14px]"
                        >
                            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                            {tr.login.signInCta}
                        </button>
                    </form>

                    <div className="mt-6 text-center text-[12.5px] text-muted-foreground">
                        {tr.login.needHelp}{" "}
                        <a href="mailto:destek@ombajans.com" className="font-semibold text-[hsl(var(--brand))] hover:underline">
                            {tr.login.contactSupport}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
